import React from "react";
import { Lightbulb, CheckCircle2, AlertTriangle, ArrowUpCircle, MessageCircleQuestion } from "lucide-react";
import { Recommendation } from "../types";
import { formatRubles } from "../utils/math_helpers";

function fmtDate(iso?: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "" : d.toLocaleDateString("ru-RU");
}

const PRIORITY: Record<string, { color: string; label: string; Icon: any }> = {
  high: { color: "text-rose-600 bg-rose-50 border-rose-100", label: "Важно", Icon: AlertTriangle },
  medium: { color: "text-amber-600 bg-amber-50 border-amber-100", label: "Желательно", Icon: ArrowUpCircle },
  low: { color: "text-slate-500 bg-slate-50 border-slate-100", label: "Опционально", Icon: ArrowUpCircle },
};

// Подпись колонок построчной разбивки (r.items) — у судебных дел и контрактов
// разный смысл одних и тех же полей (номер дела vs контрагент, срок исполнения
// vs плановая дата завершения), без заголовка строки читаются как голые цифры.
const ITEMS_HEADER: Record<string, [string, string, string]> = {
  legal: ["Дело", "Сумма иска", "Дата подачи"],
  contracts: ["Контракт", "Сумма", "Дата завершения"],
};

// Откуда пришла рекомендация — видно, когда их собирают вместе (сводный план
// в CompareTab/AiAgent смешивает методики; внутри одной вкладки бейдж один и тот же,
// но тоже подписывает происхождение совета).
const AREA: Record<string, string> = {
  anyskin: "ANYskin",
  credit: "Кредитоспособность",
  solvency: "Платёжеспособность",
  score: "Скоринг",
  krylov: "Углублённый анализ",
  legal: "Юридические риски",
  contracts: "Контракты",
  docs: "Документы",
  finance: "Финансы",
  capital: "Капитал",
};

/** Обязательный вывод по методике + список рекомендаций «что улучшить». */
export default function AdviceBlock({
  conclusion,
  recommendations,
  title = "Вывод и рекомендации",
  onAskAssistant,
}: {
  conclusion?: string;
  recommendations?: Recommendation[];
  title?: string;
  // Кнопка «Подробнее» — вопрос уходит в ИИ-ассистента, который разворачивает
  // рекомендацию по данным клиента. Без колбэка кнопка не показывается.
  onAskAssistant?: (question: string) => void;
}) {
  if (!conclusion && (!recommendations || recommendations.length === 0)) return null;
  return (
    <div className="mt-5 bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
      <div className="px-5 py-3 border-b border-slate-50 bg-slate-50/60 flex items-center gap-2">
        <Lightbulb className="w-4 h-4 text-indigo-600" />
        <h4 className="font-bold font-display text-sm text-slate-900">{title}</h4>
      </div>
      <div className="p-5 space-y-4">
        {conclusion && (
          <div className="flex gap-2.5 text-sm text-slate-700 leading-relaxed">
            <CheckCircle2 className="w-4 h-4 text-indigo-500 shrink-0 mt-0.5" />
            <p>{conclusion}</p>
          </div>
        )}
        {recommendations && recommendations.length > 0 && (
          <div className="space-y-2">
            <span className="text-[10px] font-mono font-bold text-slate-400 uppercase tracking-widest">Как улучшить</span>
            {recommendations.map((r, i) => {
              const p = PRIORITY[r.priority] || PRIORITY.low;
              return (
                <div key={i} className="flex gap-2.5 items-start border border-slate-100 rounded-xl p-3.5">
                  <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded border shrink-0 inline-flex items-center gap-1 ${p.color}`}>
                    <p.Icon className="w-3 h-3" /> {p.label}
                  </span>
                  <div className="space-y-1 min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <p className="text-sm font-bold text-slate-800">{r.action}</p>
                      {AREA[r.area] && (
                        <span className="t-micro font-semibold text-slate-400 bg-slate-50 border border-slate-100 px-1.5 py-0.5 rounded">
                          {AREA[r.area]}
                        </span>
                      )}
                    </div>
                    <p className="text-[13px] text-slate-600 leading-relaxed">{r.detail}</p>
                    {r.items && r.items.length > 0 && (() => {
                      const [hLabel, hAmount, hDate] = ITEMS_HEADER[r.area] || ["Запись", "Сумма", "Дата"];
                      return (
                        <div className="mt-1.5 border-t border-slate-50 pt-1.5">
                          <div className="hidden sm:grid grid-cols-[1fr_auto_auto] gap-x-3 text-[10px] font-bold text-slate-400 uppercase tracking-wide px-0.5 pb-1">
                            <span>{hLabel}</span>
                            <span className="text-right">{hAmount}</span>
                            <span className="text-right">{hDate}</span>
                          </div>
                          <ul className="space-y-1.5">
                            {r.items.map((it, j) => (
                              <li key={j} className="sm:grid sm:grid-cols-[1fr_auto_auto] sm:gap-x-3 sm:items-baseline text-[12px]">
                                <span className="block text-slate-700 font-semibold leading-snug break-words">
                                  {it.label}{it.note ? <span className="text-slate-400 font-normal"> · {it.note}</span> : null}
                                </span>
                                <span className="block sm:text-right text-slate-500 font-mono whitespace-nowrap">
                                  <span className="sm:hidden text-slate-400 font-sans">{hAmount}: </span>{formatRubles(it.amount)}
                                </span>
                                <span className="block sm:text-right text-slate-500 font-mono whitespace-nowrap">
                                  <span className="sm:hidden text-slate-400 font-sans">{hDate}: </span>{fmtDate(it.date) || "—"}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      );
                    })()}
                    {onAskAssistant && (
                      <button
                        onClick={() =>
                          onAskAssistant(
                            `Объясни подробнее рекомендацию «${r.action}» по текущему анализу: ${r.detail}` +
                              (r.bookPage != null ? ` (методическое обоснование — с. ${r.bookPage} источника)` : "")
                          )
                        }
                        className="flex items-center gap-1 text-[11px] font-bold text-indigo-600 hover:text-indigo-800 cursor-pointer pt-0.5"
                      >
                        <MessageCircleQuestion className="w-3.5 h-3.5 shrink-0" /> Подробнее у ассистента
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
